import { db } from '../config/firebase';
import { 
  collection, 
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp 
} from 'firebase/firestore';
import { auth } from '../config/firebase';

class MessageService {
  static async sendMessage({ 
    senderId, 
    receiverId, 
    message, 
    productId 
  }) {
    try {
      console.log('=== Starting Message Send Process ===');
      
      if (!senderId || !receiverId || !message || !productId) {
        console.error('Missing required parameters:', { senderId, receiverId, message, productId });
        throw new Error('Missing required parameters for sending message');
      }

      // Check notification settings FIRST, before any other operations
      console.log('=== Checking Notification Settings ===');
      const settingsDoc = await getDoc(doc(db, 'userSettings', receiverId));
      const settings = settingsDoc.exists() ? settingsDoc.data() : null;

      // Strict checking of notification settings
      const notificationsEnabled = settings?.notifications?.enabled === true;
      const messagesEnabled = settings?.notifications?.messages === true;
      const shouldCreateNotification = notificationsEnabled && messagesEnabled;

      console.log('Notification settings check:', {
        receiverId,
        notificationsEnabled,
        messagesEnabled,
        shouldCreateNotification,
        rawSettings: settings?.notifications
      });

      const batch = writeBatch(db);

      // Get sender info
      const senderDoc = await getDoc(doc(db, 'users', senderId));
      const senderData = senderDoc.data();
      const senderName = senderData?.name || 'Anonymous';
      const senderPhoto = senderData?.photoURL;

      // Create or update conversation
      let conversationId;
      const conversationsRef = collection(db, 'conversations');
      const existingConversations = await getDocs(
        query(
          conversationsRef,
          where('participants', 'array-contains', senderId),
          where('productId', '==', productId)
        )
      );

      let conversation = existingConversations.docs.find(doc => 
        doc.data().participants.includes(receiverId)
      );

      if (conversation) {
        conversationId = conversation.id;
      } else {
        const conversationRef = doc(collection(db, 'conversations'));
        conversationId = conversationRef.id;
        
        batch.set(conversationRef, {
          participants: [senderId, receiverId],
          productId: productId,
          lastMessage: message,
          lastMessageTime: serverTimestamp(),
          lastSenderName: senderName,
          lastSenderPhoto: senderPhoto,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        });
      }

      // Create message
      const messageRef = doc(collection(db, 'messages'));
      batch.set(messageRef, {
        conversationId,
        senderId,
        senderName,
        senderPhoto,
        receiverId,
        productId,
        message,
        read: false,
        createdAt: serverTimestamp(),
      });

      // Update conversation
      batch.update(doc(db, 'conversations', conversationId), {
        lastMessage: message,
        lastMessageTime: serverTimestamp(),
        lastSenderName: senderName,
        lastSenderPhoto: senderPhoto,
        updatedAt: serverTimestamp(),
      });

      // Only create notification if BOTH settings are explicitly true
      if (shouldCreateNotification) {
        console.log('Creating notification - both settings are true');
        const notificationRef = doc(collection(db, 'notifications'));
        batch.set(notificationRef, {
          type: 'message',
          receiverId,
          senderId,
          senderName,
          senderPhoto,
          title: 'New Message',
          message,
          conversationId,
          productId,
          read: false,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
      } else {
        console.log('Skipping notification - settings disabled:', {
          notificationsEnabled,
          messagesEnabled,
          reason: !settingsDoc.exists() ? 'No settings document' : 'Settings disabled'
        });
      }

      // Commit all changes in one batch
      await batch.commit();
      return { conversationId, messageId: messageRef.id };
    } catch (error) {
      console.error('Error in sendMessage:', error);
      console.error('Stack:', error.stack);
      throw error;
    }
  }
}

export default MessageService; 